"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";

const otheryegna = [
  {
    title: "Youth Development Loan",
    description:
      "Aimed at supporting young and productive citizens with financial and technical assistance, this loan is approved exclusively at the head office and focuses on empowering youth entrepreneurs.",
    image: "/projects/youth.jpg",
  },
  {
    title: "Tibeb/Art Loan",
    description:
      "A specialized loan for artists to fund book publishing, music production, and art galleries. Disbursement can be one-time or phased, with a maximum term of 36 months and monthly or quarterly repayments.",
    image: "/projects/tibebe1.jpg",
  },
  {
    title: "Urban Agriculture and Innovation Loan",
    description:
      "This loan supports agricultural activities in urban areas, including oxen, poultry, dairy, fattening, animal husbandry, irrigation, land rent, handicrafts, and beekeeping. The loan term ranges from 12 to 36 months, catering to small-scale farmers and agribusinesses.",
    image: "/projects/urbanagricultural76.jpg",
  },
  {
    title: "Public Association Loan",
    description:
      "Yegna MFI collaborates with governmental institutions and public associations (Idir, Shemachoch, Equib) to provide low-interest loans. Loan size is based on customer savings, capped at 1% of MFI's capital, and requires 150% collateral coverage. Terms range from 1 to 24 months, with monthly or quarterly repayments.",
    image: "/projects/shemachoch.png",
  },
];

const projects = [
  {
    title: "ETEGE",
    description:
      "Yegna MFI provides financial support to women entrepreneurs by offering low-interest, long-term loans for startups and business expansion. These loans are exclusively for women-led enterprises, with interest rates lower than conventional rates. Loan terms range from 12 to 36 months, with repayment on a monthly or quarterly basis.",
    image: "/projects/tame.png",
  },
];

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const text = "Development Financing Services";

export default function ServicedevCards() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  return (
    <div className="mt-30 mb-20 space-y-16 pt-28 overflow-x-hidden sm:pt-10 md:pt-6 lg:pt-0">
      {/* Hero Section */}
      <div className="relative w-full overflow-hidden py-24 md:py-32" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg
            className="h-full w-full"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 L100,0 L100,100 L0,100 Z"
              fill="none"
              stroke={colors.white}
              strokeWidth="0.5"
              strokeDasharray="2 2"
            />
          </svg>
        </div>

        <div className="container relative mx-auto px-6">
          <h1 className="text-center font-orbitron text-4xl font-bold text-white sm:text-5xl md:text-6xl lg:text-7xl">
            <span className="relative inline-block">
              {text.substring(0, currentIndex)}
              <motion.span
                className="absolute -right-3 top-0 h-12 w-1 bg-white"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scaleY: [1, 1.2, 1],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 0.8,
                  ease: "easeInOut",
                }}
              />
            </span>
          </h1>

          <div className="mt-12 flex justify-center space-x-4">
            {[1, 2, 3].map((item) => (
              <motion.div
                key={item}
                className="h-2 rounded-full bg-white/30"
                initial={{ width: 0 }}
                animate={{ width: item % 2 === 0 ? "40px" : "20px" }}
                transition={{
                  duration: 0.6,
                  delay: item * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />
            ))}
          </div>
        </div>

        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-12 overflow-hidden">
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="h-full w-full"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
              fill={colors.white}
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
              fill={colors.white}
            ></path>
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              fill={colors.white}
            ></path>
          </svg>
        </div>
      </div>

      {/* Projects Section */}
      <div className="mt-2 px-4 lg:mx-auto lg:max-w-[1500px]" id="projects">
        <div className="py-5">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-16 text-center text-4xl font-bold sm:text-5xl"
            style={{ color: colors.primary }}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                Development Financing Services
              </span>
              <motion.span
                className="absolute bottom-0 left-1/2 h-1 w-24 -translate-x-1/2 transform bg-gradient-to-r"
                style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          <div className="relative">
            {/* Decorative background elements */}
            <div className="absolute -left-20 top-1/4 h-64 w-64 rounded-full opacity-50 blur-xl" style={{ backgroundColor: `${colors.primary}10` }}></div>
            <div className="absolute -right-20 bottom-1/4 h-48 w-48 rounded-full opacity-20 blur-xl" style={{ backgroundColor: `${colors.primaryLight}30` }}></div>

            <div className="space-y-32">
              {projects.map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative z-10"
                >
                  {/* Timeline indicator */}
                  <div className="absolute left-1/2 top-0 hidden h-full w-0.5 -translate-x-1/2 transform md:block" style={{ background: `linear-gradient(to bottom, transparent, ${colors.primary}, transparent)` }}></div>

                  <div className="flex flex-col items-center md:flex-row md:items-stretch">
                    {/* Image column */}
                    <motion.div
                      className="relative md:w-5/12"
                      initial={{ x: -50, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className="relative aspect-video overflow-hidden rounded-xl shadow-2xl" style={{ border: `4px solid ${colors.white}` }}>
                        <Image
                          src={project.image}
                          alt={project.title}
                          fill
                          className="object-cover transition-all duration-500 hover:scale-105"
                        />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${colors.primary}30, transparent)` }}></div>
                      </div>
                      <motion.div
                        className="absolute -bottom-6 -left-6 h-16 w-16 rounded-full"
                        style={{ backgroundColor: `${colors.primaryLight}20` }}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ delay: 0.4 }}
                      />
                    </motion.div>

                    {/* Spacer column */}
                    <div className="hidden w-1/12 md:block"></div>

                    {/* Content column */}
                    <motion.div
                      className="mt-8 md:mt-0 md:w-6/12"
                      initial={{ x: 50, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <div className="relative rounded-lg p-8 shadow-lg" style={{ backgroundColor: colors.white, boxShadow: `0 10px 40px -15px ${colors.primary}30` }}>
                        <motion.h3
                          className="text-3xl font-bold"
                          style={{ color: colors.primary }}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          {project.title}
                        </motion.h3>

                        <motion.div
                          className="my-4 h-1 w-16"
                          style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          transition={{ delay: 0.4 }}
                        />

                        <motion.p
                          className="mb-6 text-lg"
                          style={{ color: colors.textDark }}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          {project.description}
                        </motion.p>

                        <div className="mb-8 flex flex-wrap gap-3">
                          {[
                            `Up to 36 months`,
                            `Competitive rates`,
                            `Flexible terms`,
                            `Quick approval`,
                          ].map((item, i) => (
                            <motion.span
                              key={i}
                              className="rounded-full px-4 py-1.5 text-sm font-medium"
                              style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
                              initial={{ scale: 0 }}
                              whileInView={{ scale: 1 }}
                              transition={{ delay: 0.6 + i * 0.1 }}
                            >
                              {item}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Development & Asset Loans Section */}
      <div className="mt-20 px-4 lg:mx-auto lg:max-w-[1500px]" id="special-loans">
        <div className="py-10">
          <div className="mt-16 space-y-28">
            {otheryegna.map((loan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} gap-10 md:gap-16`}
              >
                {/* Image Section */}
                <motion.div
                  className="group relative md:w-1/2"
                  whileHover={{ scale: 0.98 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative aspect-video overflow-hidden rounded-xl shadow-lg">
                    <Image
                      src={loan.image}
                      alt={loan.title}
                      fill
                      className="object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="pointer-events-none absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent, transparent, ${colors.primary}20)` }} />
                  </div>
                  <motion.div
                    className="absolute -bottom-4 -left-4 h-16 w-16 rounded-full"
                    style={{ backgroundColor: `${colors.primaryLight}20` }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.4 }}
                  />
                  <motion.div
                    className="absolute -right-4 -top-4 h-12 w-12 rounded-lg"
                    style={{ backgroundColor: `${colors.primary}20` }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.6 }}
                  />
                </motion.div>

                {/* Content Section */}
                <motion.div
                  className="md:w-1/2"
                  initial={{ x: index % 2 === 0 ? 50 : -50 }}
                  whileInView={{ x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex items-start">
                    <motion.div
                      className="mr-4 h-8 w-1"
                      style={{ background: `linear-gradient(to bottom, ${colors.primary}, ${colors.primaryLight})` }}
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div>
                      <motion.h3
                        className="text-3xl font-bold"
                        style={{ color: colors.primary }}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {loan.title}
                      </motion.h3>
                      <motion.div
                        className="mt-3 h-1 w-16"
                        style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ delay: 0.4 }}
                      />
                    </div>
                  </div>

                  <motion.p
                    className="mt-6 text-lg"
                    style={{ color: colors.textDark }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {loan.description}
                  </motion.p>

                  <motion.div
                    className="mt-8"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex flex-wrap gap-3">
                      {[
                        `Terms: 12-36 months`,
                        `Collateral: Flexible`,
                        `Repayment: Monthly`,
                      ].map((item, i) => (
                        <motion.span
                          key={i}
                          className="rounded-full px-4 py-1.5 text-sm font-medium"
                          style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          transition={{ delay: 0.2 + i * 0.1 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}